import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DatenschutzPage() {
  const [c, setC] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/company-settings`).then(r => setC(r.data)).catch(() => {});
  }, []);

  usePageSeo({
    title: "Datenschutzerklärung | LAB336",
    description: "Datenschutzerklärung von LAB336 gemäss dem Schweizer Datenschutzgesetz (nDSG). Informationen zur Bearbeitung Ihrer Personendaten.",
    canonical: `${BASE_URL}/datenschutz`,
  });

  if (!c) return <div className="min-h-screen bg-white" />;

  return (
    <div className="min-h-screen bg-white" data-testid="datenschutz-page">
      <header className="bg-lab-bg-dark">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </Link>
          <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white">
            Datenschutzerklärung
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none text-lab-text leading-relaxed space-y-8">

          <p className="text-lab-text-muted italic">
            Gestützt auf das Schweizer Datenschutzgesetz (nDSG, in Kraft seit 1. September 2023) informieren wir Sie nachfolgend über die Bearbeitung von Personendaten im Zusammenhang mit unserer Website und unseren Dienstleistungen.
          </p>
          <p className="text-lab-text-muted text-sm">Stand: Februar 2026</p>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">1. Verantwortlicher</h2>
            <p className="text-lab-text-muted" data-testid="datenschutz-responsible">
              Verantwortlich für die Datenbearbeitung ist:<br />
              {c.company_name}<br />
              {c.street}, {c.zip_city}, {c.country}<br />
              E-Mail: <a href={`mailto:${c.email}`} className="text-lab-accent hover:underline">{c.email}</a><br />
              Tel: <a href={`tel:${c.phone?.replace(/\s/g, "")}`} className="text-lab-accent hover:underline">{c.phone}</a>
            </p>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">2. Erhobene Personendaten</h2>
            <p className="text-lab-text-muted">
              Wir bearbeiten folgende Personendaten, die Sie uns freiwillig mitteilen oder die beim Besuch unserer Website technisch anfallen:
            </p>
            <ul className="list-disc pl-6 text-lab-text-muted space-y-1 mt-2">
              <li>Kontaktdaten: Name, E-Mail-Adresse, Telefonnummer, Unternehmen</li>
              <li>Kommunikationsdaten: Nachrichten über Kontaktformular, Terminbuchungen</li>
              <li>Newsletter: E-Mail-Adresse bei Anmeldung</li>
              <li>Technische Daten: IP-Adresse, Browsertyp, Betriebssystem, Seitenaufrufe, Zugriffszeiten (über PostHog Analytics)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">3. Zweck und Rechtsgrundlage der Bearbeitung</h2>
            <p className="text-lab-text-muted">
              Wir bearbeiten Ihre Personendaten zu folgenden Zwecken und gestützt auf folgende Legitimationsgrundlagen gemäss Art. 31 nDSG:
            </p>
            <ul className="list-disc pl-6 text-lab-text-muted space-y-1 mt-2">
              <li><strong>Vertragserfüllung und vorvertragliche Massnahmen</strong> (Art. 31 Abs. 2 lit. a nDSG): Bearbeitung von Anfragen, Terminvereinbarungen, Angebotserstellung und Projektdurchführung.</li>
              <li><strong>Einwilligung</strong> (Art. 31 Abs. 1 nDSG): Versand des Newsletters. Sie können Ihre Einwilligung jederzeit widerrufen.</li>
              <li><strong>Überwiegende berechtigte Interessen</strong> (Art. 31 Abs. 1 nDSG): Analyse und Verbesserung unserer Website, Gewährleistung der IT-Sicherheit.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">4. Weitergabe an Dritte</h2>
            <p className="text-lab-text-muted">
              Wir geben Personendaten nur weiter, wenn dies für die genannten Zwecke erforderlich ist. Dabei setzen wir folgende Auftragsbearbeiter ein:
            </p>
            <ul className="list-disc pl-6 text-lab-text-muted space-y-1 mt-2">
              <li><strong>PostHog</strong> (EU): Web-Analytics — Auftragsbearbeitungsvertrag vorhanden</li>
              <li><strong>Resend</strong> (USA): E-Mail-Versand — mit angemessenen Datenschutzgarantien</li>
            </ul>
            <p className="text-lab-text-muted mt-2">
              Bei Datenübermittlungen ins Ausland stellen wir sicher, dass ein angemessenes Datenschutzniveau besteht (Art. 16 f. nDSG), z.B. durch Standardvertragsklauseln oder die Prüfung gemäss der Länderliste des EDÖB.
            </p>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">5. Datenbearbeitung auf der Website</h2>
            <h3 className="font-semibold text-lab-primary text-base mt-4 mb-2">Cookies</h3>
            <p className="text-lab-text-muted">
              Unsere Website verwendet technisch notwendige Cookies sowie Analyse-Cookies (PostHog). Sie können die Cookie-Einstellungen jederzeit über den Link im Footer anpassen oder Cookies in Ihren Browsereinstellungen deaktivieren.
            </p>
            <h3 className="font-semibold text-lab-primary text-base mt-4 mb-2">Kontaktformular und Terminbuchung</h3>
            <p className="text-lab-text-muted">
              Wenn Sie unser Kontaktformular nutzen oder einen Termin buchen, werden Ihre eingegebenen Daten zur Bearbeitung Ihrer Anfrage gespeichert.
            </p>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">6. Automatisierte Einzelentscheidungen und Profiling</h2>
            <p className="text-lab-text-muted">
              Wir verwenden keine automatisierten Einzelentscheidungen im Sinne von Art. 21 nDSG. Es findet kein Profiling statt, das rechtliche Auswirkungen auf Sie hat oder Sie in ähnlicher Weise erheblich beeinträchtigt.
            </p>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">7. Speicherdauer</h2>
            <p className="text-lab-text-muted">
              Wir speichern Personendaten nur so lange, wie dies für die jeweiligen Zwecke erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen. Kontaktanfragen werden in der Regel nach 2 Jahren gelöscht, sofern keine laufende Geschäftsbeziehung besteht.
            </p>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">8. Ihre Rechte gemäss nDSG</h2>
            <p className="text-lab-text-muted">
              Sie haben folgende Rechte bezüglich Ihrer Personendaten:
            </p>
            <ul className="list-disc pl-6 text-lab-text-muted space-y-1 mt-2">
              <li><strong>Auskunftsrecht</strong> (Art. 25 nDSG): Sie können Auskunft über Ihre gespeicherten Daten verlangen.</li>
              <li><strong>Recht auf Datenherausgabe/-übertragung</strong> (Art. 28 nDSG): Sie können verlangen, dass Ihnen Ihre Daten in einem gängigen Format herausgegeben werden.</li>
              <li><strong>Recht auf Berichtigung</strong> (Art. 32 Abs. 1 nDSG): Sie können unrichtige Daten korrigieren lassen.</li>
              <li><strong>Recht auf Löschung</strong>: Sie können die Löschung Ihrer Daten verlangen, sofern keine gesetzliche Aufbewahrungspflicht dem entgegensteht.</li>
              <li><strong>Widerspruchsrecht</strong>: Sie können der Bearbeitung Ihrer Daten jederzeit widersprechen.</li>
            </ul>
            <p className="text-lab-text-muted mt-3">
              Zur Ausübung Ihrer Rechte wenden Sie sich bitte an: <a href={`mailto:${c.email}`} className="text-lab-accent hover:underline">{c.email}</a>
            </p>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">9. Datensicherheit</h2>
            <p className="text-lab-text-muted">
              Wir treffen angemessene technische und organisatorische Massnahmen, um Ihre Personendaten vor unbefugtem Zugriff, Verlust oder Missbrauch zu schützen (Art. 8 nDSG). Dazu gehören verschlüsselte Datenübertragung (TLS/SSL), regelmässige Sicherheitsupdates und Zugangsbeschränkungen.
            </p>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">10. Aufsichtsbehörde</h2>
            <p className="text-lab-text-muted">
              Zuständige Aufsichtsbehörde für den Datenschutz in der Schweiz ist der Eidgenössische Datenschutz- und Öffentlichkeitsbeauftragte (EDÖB):<br />
              <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer" className="text-lab-accent hover:underline">www.edoeb.admin.ch</a>
            </p>
          </section>

          <section>
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-3">11. Änderungen</h2>
            <p className="text-lab-text-muted">
              Wir behalten uns vor, diese Datenschutzerklärung jederzeit anzupassen. Die aktuelle Version ist auf unserer Website verfügbar.
            </p>
          </section>

        </div>
      </main>

      <footer className="bg-lab-bg-dark border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between">
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} LAB 336. Alle Rechte vorbehalten.
          </p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-sm transition-colors">
            Startseite
          </Link>
        </div>
      </footer>
    </div>
  );
}
